import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { type SEOHandle } from '@nasa-gcn/remix-seo'

import {
	json,
	redirect,
	type ActionFunctionArgs,
	type MetaFunction,
} from '@remix-run/node'
import { Link, useFetcher } from '@remix-run/react'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { GeneralErrorBoundary } from '#app/components/error-boundary'
import { ErrorList, Field } from '#app/components/forms'
import { StatusButton } from '#app/components/ui/status-button'
import { ForgotPasswordEmail } from '#app/emails/email-forgot-password'
import { prisma } from '#app/utils/db.server'
import { sendEmail } from '#app/utils/email.server'
import { checkHoneypot } from '#app/utils/honeypot.server'
import { EmailSchema, UsernameSchema } from '#app/utils/user-validation'
import { prepareVerification } from './verify.server.tsx'

export const handle: SEOHandle = {
	getSitemapEntries: () => null,
}

const ForgotPasswordSchema = z.object({
	usernameOrEmail: z.union([EmailSchema, UsernameSchema]),
})

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()
	checkHoneypot(formData)
	const submission = await parseWithZod(formData, {
		schema: ForgotPasswordSchema.superRefine(async (data, ctx) => {
			const user = await prisma.user.findFirst({
				where: {
					OR: [
						{ email: data.usernameOrEmail },
						{ username: data.usernameOrEmail },
					],
				},
				select: { id: true },
			})
			if (!user) {
				ctx.addIssue({
					path: ['usernameOrEmail'],
					code: z.ZodIssueCode.custom,
					message: 'No user exists with this username or email',
				})
				return
			}
		}),
		async: true,
	})
	if (submission.status !== 'success') {
		return json(
			{ result: submission.reply() },
			{
				status: submission.status === 'error' ? 400 : 200,
			},
		)
	}

	const { usernameOrEmail } = submission.value

	const user = await prisma.user.findFirstOrThrow({
		where: { OR: [{ email: usernameOrEmail }, { username: usernameOrEmail }] },
		select: { email: true, username: true },
	})

	const { verifyUrl, redirectTo, otp } = await prepareVerification({
		period: 10 * 60,
		request,
		type: 'reset-password',
		target: usernameOrEmail,
	})

	const response = await sendEmail({
		to: user.email,
		subject: `Réinitialisation de votre mot de passe - PLQ`,
		react: (
			<ForgotPasswordEmail onboardingUrl={verifyUrl.toString()} otp={otp} />
		),
	})

	if (response.status === 'success') {
		return redirect(redirectTo.toString())
	} else {
		return json(
			{
				result: submission.reply({ formErrors: [response.error.message] }),
			},
			{
				status: 500,
			},
		)
	}
}
export const meta: MetaFunction = () => {
	return [{ title: 'Récupération de mot de passe de PLQ' }]
}

export default function ForgotPasswordPage() {
	const forgotPassword = useFetcher<typeof action>()

	const [form, fields] = useForm({
		id: 'forgot-password-form',
		constraint: getZodConstraint(ForgotPasswordSchema),
		lastResult: forgotPassword.data?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: ForgotPasswordSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<div className="flex flex-col space-y-6">
			<div className="space-y-4">
				<h1 className="text-4xl font-bold">Mot de passe oublié</h1>
				<p className="pb-4 pt-4 text-lg text-muted-foreground">
					Un courriel contenant un lien pour changer votre mot de passe vous
					sera envoyé.
				</p>

				<forgotPassword.Form method="POST" {...getFormProps(form)}>
					<HoneypotInputs />
					<Field
						labelProps={{ children: 'Courriel' }}
						inputProps={{
							placeholder: 'Entrez votre courriel',
							...getInputProps(fields.usernameOrEmail, { type: 'text' }),
							autoFocus: true,
							className: 'mt-3 text-lg py-7',
						}}
						className="gap-4"
						errors={fields.usernameOrEmail.errors}
					/>

					<ErrorList errors={form.errors} id={form.errorId} />

					<StatusButton
						className="w-full"
						status={
							forgotPassword.state === 'submitting'
								? 'pending'
								: (form.status ?? 'idle')
						}
						type="submit"
						disabled={forgotPassword.state !== 'idle'}
					>
						Envoyer
					</StatusButton>
				</forgotPassword.Form>
			</div>

			<p>
				<Link to="/login"> Retour à la connexion</Link>
			</p>
			<p className="text-sm text-gray-500">
				© 2025 <Link to="">PLQ.</Link> Tous droits réservés.
			</p>
		</div>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
